<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-md-12">
        <div class="card shadow-lg">
          <div class="card-header pb-0">
            <div class="d-flex align-items-center">
              <h6 class="mb-0">Editar Cliente: {{ client.name || 'Cargando...' }}</h6>
            </div>
          </div>
          <div class="card-body">
            <div v-if="loading" class="text-center p-5">
              <div class="spinner-border text-success" role="status">
                <span class="visually-hidden">Cargando...</span>
              </div>
            </div>
            <form v-else @submit.prevent="submitClient">
              <h6 class="text-uppercase text-body text-sm font-weight-bolder">Datos de Identificación</h6>
              <div class="row">
                <div class="col-md-6">
                  <label for="document" class="form-control-label">Documento / Identificación</label>
                  <argon-input id="document" type="text" v-model="client.document" placeholder="Número de Documento" />
                </div>

                <div class="col-md-12 mt-3">
                  <label for="clientName" class="form-control-label">Nombres y Apellidos / Razón Social</label>
                  <argon-input id="clientName" type="text" v-model="client.name" />
                  <small v-if="submitted && v$.client.name.$error" class="text-danger">Nombre es requerido</small>
                </div>

                <div class="col-md-6 mt-3">
                  <label for="gender" class="form-control-label">Género</label>
                  <argon-select id="gender" class="form-control" v-model="client.gender" :choices="genders" />
                  <small v-if="submitted && v$.client.gender.$error" class="text-danger">Género es requerido</small>
                </div>

                <div class="col-md-6 mt-3">
                  <label for="phone" class="form-control-label">Teléfono de Contacto</label>
                  <argon-input id="phone" type="text" v-model="client.phone" />
                </div>

                <div class="col-md-6 mt-3">
                  <label for="email" class="form-control-label">Correo Electrónico</label>
                  <argon-input id="email" type="text" v-model="client.email" />
                </div>

                <div class="col-md-6 mt-3">
                  <label for="nationality" class="form-control-label">Nacionalidad</label>
                  <argon-select id="nationality" class="form-control" v-model="client.nationality" :choices="nationalities" />
                </div>

                <div class="col-md-12 mt-3">
                  <label for="address" class="form-control-label">Dirección de Residencia / Cobro</label>
                  <argon-textarea id="address" type="text" v-model="client.address" />
                  <small v-if="submitted && v$.client.address.$error" class="text-danger">Dirección es requerida</small>
                </div>

                <hr class="horizontal dark mt-4 mb-3" />

                <div class="col-md-12 mt-3">
                  <label class="form-label font-weight-bold">Ubicación Geográfica (Punto de Visita)</label>
                  <div id="task-map" class="border-radius-lg shadow-sm mb-2" style="height: 300px; z-index: 1;"></div>
                  <div class="row">
                    <div class="col-6">
                      <argon-input v-model="client.latitude" placeholder="Latitud" readonly size="sm" />
                    </div>
                    <div class="col-6">
                      <argon-input v-model="client.longitude" placeholder="Longitud" readonly size="sm" />
                    </div>
                  </div>
                </div>

                <hr class="horizontal dark mt-4 mb-3" />

                <div class="col-md-12 mt-3">
                  <label class="form-control-label">Archivos (Documentos y Foto)</label>
                </div>

                <div class="col-md-4 text-center">
                  <label class="form-control-label">Foto del cliente</label>
                  <div class="image-upload-area" @click="uploadPhoto('photo')">
                    <img :src="client.photo_url || fileUpload" alt="Foto cliente" class="img-thumbnail" />
                    <input type="file" style="display: none" ref="clientPhotoFile" accept="image/*" @change="onFileChange('photo', $event)">
                  </div>
                </div>

                <div class="col-md-4 text-center">
                  <label class="form-control-label">Documento Frontal</label>
                  <div class="image-upload-area" @click="uploadPhoto('documentFront')">
                    <img :src="client.document_front_url || fileUpload" alt="Doc Frontal" class="img-thumbnail" />
                    <input type="file" style="display: none" ref="clientFrontFile" accept="image/*" @change="onFileChange('front', $event)">
                  </div>
                </div>

                <div class="col-md-4 text-center">
                  <label class="form-control-label">Documento Trasero</label>
                  <div class="image-upload-area" @click="uploadPhoto('documentBack')">
                    <img :src="client.document_back_url || fileUpload" alt="Doc Trasero" class="img-thumbnail" />
                    <input type="file" style="display: none" ref="clientBackFile" accept="image/*" @change="onFileChange('back', $event)">
                  </div>
                </div>
              </div>

              <div class="d-flex gap-3">
                <argon-button type="submit" class="mt-4" color="success" fullWidth size="lg" :disabled="isUpdating">
                  {{ isUpdating ? 'Actualizando...' : 'Actualizar Cliente' }}
                </argon-button>
                <argon-button type="button" class="mt-4" color="secondary" fullWidth size="lg" @click="$router.push('/clients')">
                  Cancelar
                </argon-button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ArgonInput from "@/components/ArgonInput.vue";
import ArgonSelect from "@/components/ArgonSelect.vue";
import ArgonTextarea from "@/components/ArgonTextarea.vue";
import ArgonButton from "@/components/ArgonButton.vue";
import { useStatics } from '@/composables/useStatics';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import axios from "axios";
import Swal from 'sweetalert2';
import fileUpload from '@/assets/img/uploadfile.jpg';
import L from 'leaflet';

const { nationalities } = useStatics();
const DEFAULT_CENTER = [13.689, -89.187];

export default {
  name: "EditClient",
  setup: () => ({ v$: useVuelidate() }),
  components: { ArgonInput, ArgonSelect, ArgonTextarea, ArgonButton },
  data() {
    return {
      map: null,
      marker: null,
      loading: true,
      isUpdating: false,
      clientId: this.$route.params.id,
      fileUpload,
      submitted: false,
      nationalities,
      client: {
        document: null,
        name: null,
        gender: null,
        email: null,
        phone: null,
        address: null,
        company: localStorage.getItem('companyId'),
        photo_url: null,
        document_front_url: null,
        document_back_url: null,
        latitude: 0,
        longitude: 0,
      },
      genders: [
        { value: 'M', text: 'Masculino' },
        { value: 'F', text: 'Femenino' },
        { value: 'O', text: 'Otro / Empresa' },
      ],
    };
  },
  validations() {
    return {
      client: {
        name: { required },
        gender: { required },
        address: { required },
      }
    }
  },
  methods: {
    async fetchClientData() {
      const token = localStorage.getItem("accessToken");
      const apiURL = this.$store.state.apiURL;
      try {
        const response = await axios.get(`${apiURL}clients/${this.clientId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        this.client = { ...this.client, ...response.data };

        // Inicializar el mapa una vez que tengamos las coordenadas
        this.$nextTick(() => {
          this.initializeMap();
        });
      } catch (error) {
        Swal.fire('Error', 'No se pudieron cargar los datos.', 'error');
        this.$router.push('/clients');
      } finally {
        this.loading = false;
      }
    },

    initializeMap() {
      // Si el cliente ya tiene coordenadas, usarlas. Si no, usar centro por defecto.
      const lat = parseFloat(this.client.latitude) || DEFAULT_CENTER[0];
      const lng = parseFloat(this.client.longitude) || DEFAULT_CENTER[1];

      this.map = L.map('task-map').setView([lat, lng], 15);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap'
      }).addTo(this.map);

      const icon = L.icon({
        iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
        iconUrl: require('leaflet/dist/images/marker-icon.png'),
        shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
        iconSize: [25, 41],
        iconAnchor: [12, 41]
      });

      this.marker = L.marker([lat, lng], { draggable: true, icon }).addTo(this.map);

      this.marker.on('dragend', () => {
        const position = this.marker.getLatLng();
        this.client.latitude = position.lat.toFixed(8);
        this.client.longitude = position.lng.toFixed(8);
      });

      this.map.on('click', (e) => {
        this.marker.setLatLng(e.latlng);
        this.client.latitude = e.latlng.lat.toFixed(8);
        this.client.longitude = e.latlng.lng.toFixed(8);
      });

      // Fix para renderizado en tabs o modales
      setTimeout(() => { this.map.invalidateSize(); }, 400);
    },

    uploadPhoto(ref) {
      if (ref === 'documentFront') this.$refs.clientFrontFile.click();
      else if (ref === 'documentBack') this.$refs.clientBackFile.click();
      else this.$refs.clientPhotoFile.click();
    },

    async onFileChange(type, event) {
      const file = event.target.files[0];
      if (!file) return;

      let formData = new FormData();
      formData.append('file', file);
      formData.append('fileName', 'client_doc');

      try {
        const result = await axios.post(this.$store.state.fileURL, formData, {
          headers: {
            "Authorization": "Basic cHJpdmF0ZV9XVXVROG1DQncxV210WHc1empIa3NGQkNZQ1E9Og==",
            'Content-Type': 'multipart/form-data'
          }
        });
        if (type === 'front') this.client.document_front_url = result.data.url;
        else if (type === 'back') this.client.document_back_url = result.data.url;
        else this.client.photo_url = result.data.url;
      } catch (err) {
        Swal.fire('Error', 'Fallo al subir archivo.', 'error');
      }
    },

    async submitClient() {
      this.submitted = true;
      this.v$.$touch();
      if (this.v$.$invalid) return;

      this.isUpdating = true;
      const token = localStorage.getItem("accessToken");
      const apiURL = this.$store.state.apiURL;

      try {
        await axios.put(`${apiURL}clients/${this.clientId}`, this.client, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        Swal.fire('Actualizado', 'Datos guardados correctamente.', 'success');
        this.$router.push('/clients');
      } catch (err) {
        Swal.fire('Error', 'Fallo al actualizar.', 'error');
      } finally {
        this.isUpdating = false;
      }
    }
  },
  mounted() {
    this.fetchClientData();
  }
};
</script>

<style scoped>
.image-upload-area {
  cursor: pointer;
  border: 2px dashed #ccc;
  padding: 10px;
  margin-top: 10px;
  border-radius: 8px;
  display: inline-block;
  width: 100%;
  background: #f8f9fa;
}
.image-upload-area img {
  max-width: 100%;
  max-height: 150px;
  object-fit: contain;
}
.image-upload-area:hover {
  border-color: #2dce89;
}
</style>